import { userApi } from '../api/userApi';
import { UserFilterParams } from '../models/user/userFilterParams';
import {
    AdjustUserPointRequest, ChangeUserPasswordRequest, DeleteUserRequest,
    UpdateUserRoleRequest, UpdateUserSubscriptionRequest,
    AdjustFeathersRequest, AdjustCreditsRequest,
} from '../models/user/userRequests';
import { useBaseQuery, useBaseMutation, useNoDataMutation, useInvalidateQueries } from './useBaseQuery';
import { PaginationData } from '../models/paginationData';
import {
    User, UserDetail, RewardHistoryItem, PointPurchaseItem, SubscriptionHistoryItem,
    UserReportItem, UserInquiryItem, UserPushHistoryItem,
    MasteredWordItem, MissionClaimItem, EppiDetail, FeatherHistoryItem, AdminAiCreditDetail,
} from '../types/user';
import {
    UserOverviewStatsResponse,
    UserProviderStatsResponse,
    UserSubscriptionStatsResponse
} from '../models/user/userResponses';

// Query Keys
export const userKeys = {
    all: ['user'] as const,
    lists: () => [...userKeys.all, 'list'] as const,
    list: (filters: UserFilterParams) => [...userKeys.lists(), filters] as const,
    details: () => [...userKeys.all, 'detail'] as const,
    detail: (id: number) => [...userKeys.details(), id] as const,
    stats: () => [...userKeys.all, 'stats'] as const,
    overviewStats: () => [...userKeys.stats(), 'overview'] as const,
    providerStats: () => [...userKeys.stats(), 'provider'] as const,
    subscriptionStats: () => [...userKeys.stats(), 'subscription'] as const,
    rewardHistories: (id: number, page: number) => [...userKeys.all, 'rewardHistories', id, page] as const,
    pointPurchases: (id: number, page: number) => [...userKeys.all, 'pointPurchases', id, page] as const,
    subscriptionHistories: (id: number, page: number) => [...userKeys.all, 'subscriptionHistories', id, page] as const,
    reports: (id: number, page: number) => [...userKeys.all, 'reports', id, page] as const,
    inquiries: (id: number, page: number) => [...userKeys.all, 'inquiries', id, page] as const,
    pushHistories: (id: number, page: number) => [...userKeys.all, 'pushHistories', id, page] as const,
    masteredWords: (id: number, page: number) => [...userKeys.all, 'masteredWords', id, page] as const,
    missionClaims: (id: number, page: number) => [...userKeys.all, 'missionClaims', id, page] as const,
    eppiDetail: (id: number) => [...userKeys.all, 'eppiDetail', id] as const,
    featherHistories: (id: number, page: number) => [...userKeys.all, 'featherHistories', id, page] as const,
    aiCreditDetail: (id: number) => [...userKeys.all, 'aiCreditDetail', id] as const,
};

// 사용자 목록 조회
export const useUserList = (params: UserFilterParams) => {
    return useBaseQuery<PaginationData<User>>(
        userKeys.list(params),
        () => userApi.getList(params),
        {
            staleTime: 30 * 1000, // 30초
        }
    );
};

// 사용자 상세 조회
export const useUser = (userId: number) => {
    return useBaseQuery<UserDetail>(
        userKeys.detail(userId),
        () => userApi.getOne(userId),
        {
            enabled: userId > 0,
            staleTime: 60 * 1000, // 1분
        }
    );
};

// 사용자 삭제
export const useDeleteUser = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ id, data }: { id: number; data: DeleteUserRequest }) => userApi.delete(id, data),
        {
            onSuccess: () => {
                invalidate(userKeys.lists());
                invalidate(userKeys.stats());
                invalidate(userKeys.details());
            },
        }
    );
};

// 사용자 복원
export const useRestoreUser = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        (id: number) => userApi.restore(id),
        {
            onSuccess: () => {
                invalidate(userKeys.lists());
                invalidate(userKeys.stats());
                invalidate(userKeys.details());
            },
        }
    );
};

// 사용자 역할 변경
export const useUpdateUserRole = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ id, data }: { id: number; data: UpdateUserRoleRequest }) =>
            userApi.updateRole(id, data),
        {
            onSuccess: () => {
                invalidate(userKeys.lists());
                invalidate(userKeys.details());
            },
        }
    );
};

// 사용자 구독 변경
export const useUpdateUserSubscription = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ id, data }: { id: number; data: UpdateUserSubscriptionRequest }) =>
            userApi.updateSubscription(id, data),
        {
            onSuccess: () => {
                invalidate(userKeys.lists());
                invalidate(userKeys.details());
                invalidate(userKeys.stats());
            },
        }
    );
};

// 사용자 포인트 조정
export const useAdjustUserPoints = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ id, data }: { id: number; data: AdjustUserPointRequest }) =>
            userApi.adjustPoints(id, data),
        {
            onSuccess: () => {
                invalidate(userKeys.lists());
                invalidate(userKeys.details());
            },
        }
    );
};

// 사용자 비밀번호 변경
export const useChangeUserPassword = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ id, data }: { id: number; data: ChangeUserPasswordRequest }) =>
            userApi.changePassword(id, data),
        {
            onSuccess: () => {
                invalidate(userKeys.details());
            },
        }
    );
};

// 포인트 획득/차감 이력 조회
export const useUserRewardHistories = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<RewardHistoryItem>>(
        userKeys.rewardHistories(userId, page),
        () => userApi.getRewardHistories(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 포인트 구매 이력 조회
export const useUserPointPurchases = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<PointPurchaseItem>>(
        userKeys.pointPurchases(userId, page),
        () => userApi.getPointPurchases(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 구독 이력 조회
export const useUserSubscriptionHistories = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<SubscriptionHistoryItem>>(
        userKeys.subscriptionHistories(userId, page),
        () => userApi.getSubscriptionHistories(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 사용자 신고 이력 조회
export const useUserReports = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<UserReportItem>>(
        userKeys.reports(userId, page),
        () => userApi.getReports(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 사용자 문의 이력 조회
export const useUserInquiries = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<UserInquiryItem>>(
        userKeys.inquiries(userId, page),
        () => userApi.getInquiries(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 사용자가 받은 푸시 이력 조회
export const useUserPushHistories = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<UserPushHistoryItem>>(
        userKeys.pushHistories(userId, page),
        () => userApi.getPushHistories(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 마스터 단어 목록 조회
export const useMasteredWords = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<MasteredWordItem>>(
        userKeys.masteredWords(userId, page),
        () => userApi.getMasteredWords(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 미션 claim 목록 조회
export const useMissionClaims = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<MissionClaimItem>>(
        userKeys.missionClaims(userId, page),
        () => userApi.getMissionClaims(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 에삐(스티커북) 상세 조회
export const useEppiDetail = (userId: number) => {
    return useBaseQuery<EppiDetail>(
        userKeys.eppiDetail(userId),
        () => userApi.getEppiDetail(userId),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// 깃털(XP) 내역 조회
export const useFeatherHistories = (userId: number, page: number) => {
    return useBaseQuery<PaginationData<FeatherHistoryItem>>(
        userKeys.featherHistories(userId, page),
        () => userApi.getFeatherHistories(userId, page),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// AI 크레딧 상세 조회 (잔액 + 이력 + 사용처 집계)
export const useAiCreditDetail = (userId: number) => {
    return useBaseQuery<AdminAiCreditDetail>(
        userKeys.aiCreditDetail(userId),
        () => userApi.getAiCreditDetail(userId),
        { enabled: userId > 0, staleTime: 30 * 1000 },
    );
};

// AI 크레딧 조정
export const useAdjustCredits = () => {
    const { invalidate } = useInvalidateQueries();
    return useBaseMutation<{ id: number; data: AdjustCreditsRequest }, AdminAiCreditDetail>(
        ({ id, data }) => userApi.adjustCredits(id, data),
        {
            onSuccess: (_, variables) => {
                invalidate(userKeys.details());
                invalidate(userKeys.aiCreditDetail(variables.id));
            },
        }
    );
};

// 깃털(XP) 조정
export const useAdjustFeathers = () => {
    const { invalidate, invalidateByPrefix } = useInvalidateQueries();
    return useBaseMutation<{ id: number; data: AdjustFeathersRequest }, UserDetail>(
        ({ id, data }) => userApi.adjustFeathers(id, data),
        {
            onSuccess: (_, variables) => {
                invalidate(userKeys.details());
                invalidateByPrefix([...userKeys.all, 'featherHistories', variables.id]);
            },
        }
    );
};

// 전체 통계 조회
export const useUserOverviewStats = () => {
    return useBaseQuery<UserOverviewStatsResponse>(
        userKeys.overviewStats(),
        () => userApi.getOverviewStats(),
        {
            staleTime: 5 * 60 * 1000, // 5분
        }
    );
};

// 제공자별 통계 조회
export const useUserProviderStats = () => {
    return useBaseQuery<UserProviderStatsResponse>(
        userKeys.providerStats(),
        () => userApi.getProviderStats(),
        {
            staleTime: 5 * 60 * 1000,
        }
    );
};

// 구독 통계 조회
export const useUserSubscriptionStats = () => {
    return useBaseQuery<UserSubscriptionStatsResponse>(
        userKeys.subscriptionStats(),
        () => userApi.getSubscriptionStats(),
        {
            staleTime: 5 * 60 * 1000,
        }
    );
};
